import { LinearProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    BarChart,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useReportingReportsByUserQuery } from '../../../../hooks/queries/useReportingQuery';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { countPropertyValues } from '../../../../utils/array';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { localStorageKeys } from '../../../../utils/enums';
import { getItemFromLocalStorage, storeItemToLocalStorage } from '../../../../utils/localStorage';
import DropDown from '../../../DropDown';
import ResponsiveCard from '../../../ResponsiveCard';
import { ActivityType } from '../../../../services/apiSLTP/dailyReports/activityTypes';

interface ReportingReportsByUserProps {
    startDate: Date;
    endDate: Date;
}

const COLORS = ['#FF8042', '#00C49F', '#FFBB28', 'blue'];

const ReportingReportsByUser: React.FC<ReportingReportsByUserProps> = ({ startDate, endDate }) => {
    const [selectedUser, setSelectedUser] = useState({ label: '', value: '' });

    const { t } = useTranslation();
    const { data: dataReportingReportsByUser, isLoading: isReportingReportsByUserLoading } = useReportingReportsByUserQuery({
        params: { startDate: startDate.toISOString(), endDate: endDate.toISOString(), userId: selectedUser.value },
    });
    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });
    const { user } = useAuth();

    const handleUserChange = ({ label, value }: { label: string; value: string }) => {
        setSelectedUser({ label, value });
        storeItemToLocalStorage(localStorageKeys.REPORTINGREPORTSUSERID, value);
    };

    const transformActivityTypesForPieChart = (activitiesTypes: ActivityType[]) => {
        const frequencyMap: Record<string, number> = {};

        activitiesTypes.forEach((activity) => {
            if (frequencyMap[activity.designation]) {
                frequencyMap[activity.designation]++;
            } else {
                frequencyMap[activity.designation] = 1;
            }
        });

        return Object.keys(frequencyMap).map((key) => ({
            name: key,
            value: frequencyMap[key],
        }));
    };

    useEffect(() => {
        const userId = getItemFromLocalStorage(localStorageKeys.REPORTINGREPORTSUSERID);
        const storedUser = dataUsers?.find((user) => user._id === userId);
        setSelectedUser({
            value: storedUser?._id || '',
            label: formatName(storedUser?.firstName, storedUser?.lastName),
        });
    }, [dataReportingReportsByUser, dataUsers]);

    return (
        <>
            <div className="bg-black text-white border-radius-10px fx-c pad-15px gap-15px">
                <DropDown
                    selectedItem={selectedUser}
                    items={(dataUsers || [])
                        .sort((a, b) => {
                            const userA = a.lastName + a.firstName;
                            const userB = b.lastName + a.firstName;
                            if (userA < userB) return -1;
                            if (userA > userB) return 1;
                            return 0;
                        })
                        .map((user) => ({
                            label: formatName(user?.firstName, user?.lastName),
                            value: user?._id,
                        }))}
                    inputLabel=""
                    handleChange={(event, value) => handleUserChange({ value: value?.value || '', label: value?.label || '' })}
                    selectClassName="h-25px "
                    formClassName="pad-0-imp "
                    loading={isUsersLoading}
                    sortItems={false}
                />
                {isReportingReportsByUserLoading && <LinearProgress color="inherit" />}
            </div>

            <div className="fx-c of-y-auto border-radius-10px fx-fg-1">
                {dataReportingReportsByUser && (
                    <div className="dashboard">
                        {/* Graphiques temporels */}
                        <ResponsiveCard title="Consommation" heightSize="small" widthSize="large">
                            <ResponsiveContainer>
                                <LineChart
                                    data={dataReportingReportsByUser.map((report) => ({
                                        ...report,
                                        date: new Date(report.date)
                                            .toLocaleDateString(t('localeDate'), {
                                                month: 'numeric',
                                                day: 'numeric',
                                            })
                                            .replace(/\b\w/g, (char) => char.toUpperCase()),
                                    }))}
                                >
                                    <XAxis dataKey="date" />
                                    <YAxis dataKey={'km'} type="number" yAxisId="1" />
                                    <YAxis dataKey={'fuel'} orientation="right" type="number" yAxisId="2" />
                                    <Line type="monotone" dataKey="fuel" stroke="#8884d8" yAxisId="2" />
                                    <Line type="monotone" dataKey="km" stroke="#82ca9d" yAxisId="1" />
                                    <Line type="monotone" dataKey="adBlue" stroke="#ffc658" yAxisId="2" />
                                    <Tooltip />
                                    <Legend />
                                </LineChart>
                            </ResponsiveContainer>
                        </ResponsiveCard>

                        {/* Graphique de distribution des activités */}
                        <ResponsiveCard title="Activités" widthSize="large">
                            <ResponsiveContainer>
                                <BarChart
                                    data={dataReportingReportsByUser.map((report) => ({
                                        ...report,
                                        date: new Date(report.date)
                                            .toLocaleDateString(t('localeDate'), {
                                                month: 'numeric',
                                                day: 'numeric',
                                            })
                                            .replace(/\b\w/g, (char) => char.toUpperCase()),
                                    }))}
                                >
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Bar dataKey="nbActivities" fill="#8884d8" stackId={'b'} />
                                    <Bar dataKey="nbActivitiesSent" fill="#82ca9d" stackId={'b'} />
                                    <Tooltip />
                                    <Legend />
                                </BarChart>
                            </ResponsiveContainer>
                        </ResponsiveCard>
                        <ResponsiveCard title="Tours de camions">
                            {/* Graphique radar pour les types d'activités */}
                            <ResponsiveContainer>
                                <RadarChart
                                    data={dataReportingReportsByUser.map((report) => ({
                                        ...report,
                                        date: new Date(report.date)
                                            .toLocaleDateString(t('localeDate'), {
                                                month: 'numeric',
                                                day: 'numeric',
                                            })
                                            .replace(/\b\w/g, (char) => char.toUpperCase()),
                                    }))}
                                >
                                    <PolarGrid />
                                    <PolarAngleAxis dataKey="date" />
                                    <PolarRadiusAxis />
                                    <Radar
                                        name={capitalizeFirstLetter(t('trucksRides'))}
                                        dataKey="nbTruckRides"
                                        stroke="#8884d8"
                                        fill="#8884d8"
                                        fillOpacity={0.6}
                                    />
                                    <Tooltip />
                                    <Legend />
                                </RadarChart>
                            </ResponsiveContainer>
                        </ResponsiveCard>
                        <ResponsiveCard title="Types d'activités">
                            {/* Graphique à secteurs pour les sites */}
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={countPropertyValues(
                                            dataReportingReportsByUser?.flatMap((report) => report.activitiesTypes),
                                            'designation'
                                        )}
                                        dataKey="number"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={1}
                                    >
                                        {countPropertyValues(
                                            dataReportingReportsByUser?.flatMap((report) => report.activitiesTypes),
                                            'designation'
                                        ).map((entry, index) => {
                                            return <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />;
                                        })}
                                    </Pie>

                                    <Tooltip />
                                    <Legend />
                                    <text
                                        x="50%"
                                        y="45%"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        style={{
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            fill: '#333',
                                        }}
                                    >
                                        {countPropertyValues(
                                            dataReportingReportsByUser?.flatMap((report) => report.activitiesTypes),
                                            'designation'
                                        ).reduce((acc, item) => acc + item.number, 0)}
                                    </text>
                                </PieChart>
                            </ResponsiveContainer>
                        </ResponsiveCard>
                        <ResponsiveCard title="Tours de camions" widthSize="large">
                            {/* Graphique des trajets de camion */}
                            <ResponsiveContainer>
                                <BarChart
                                    data={dataReportingReportsByUser.map((report) => ({
                                        ...report,
                                        date: new Date(report.date)
                                            .toLocaleDateString(t('localeDate'), {
                                                month: 'numeric',
                                                day: 'numeric',
                                            })
                                            .replace(/\b\w/g, (char) => char.toUpperCase()),
                                    }))}
                                >
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Bar dataKey="nbTruckRides" name={'nombre de tours'} fill="#ffc658" />
                                    <Tooltip />
                                    <Legend />
                                </BarChart>
                            </ResponsiveContainer>
                        </ResponsiveCard>
                    </div>
                )}
            </div>
        </>
    );
};

export default ReportingReportsByUser;
